import { useState, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { IconUsersGroup } from "@tabler/icons-react";

import "./styles.css";
import { useCommunity } from "contexts/community";
import { useScreen } from "Screen/ScreenContext";

import { UserAvatar } from "@components/Community";

import api from "@helpers/api";
import { AlternateEmailRounded, FingerprintRounded, MailRounded, PhoneRounded } from "@mui/icons-material";
import { SubscriptionTable } from "@components/Community/Tables/SubscriptionTable";
import { ChargesTable } from "@components/Community/Tables/ChargesTable";
import UserLogs from "./components/UserLogs";

import Parser from '@helpers/parser';

export default function Member() {
    const { t } = useTranslation("member");
    let { id } = useParams();

    const [info, setInfo] = useState(null);

    const { loading: communityLoading } = useCommunity();
    const { setBreadcrumb } = useScreen();

    document.title = t("profile.documentTitle");

    useEffect(() => {
        if (!id) window.navigate("/communities");
    }, [id]);

    useEffect(() => {
        setBreadcrumb([
            { label: t("profile.breadcrumbMembers"), href: "/banking/subscriptions", icon: <IconUsersGroup size={18} /> },
            {
                label: info?.username ?? t("common:actions.loading"),
                icon: info ? <UserAvatar user={info} size={24} /> : null,
            },
        ]);
        return () => setBreadcrumb(null);
    }, [info?.username, info?.avatar, t]);

    useEffect(() => {
        if (!communityLoading) loadUser();
    }, [id, communityLoading]);


    async function loadUser() {
        try {
            const { data } = await api.get("/community/membership/userInfo/" + id);
            // New MemberDetails DTO nests the account under `user`
            // ({ user, role, subscriptions, timestamps })
            setInfo({ ...(data.user || {}), role: data.role });
        } catch (e) {
            console.log(e);
        }
    }

    const Container = useMemo(
        () => (
            <>
                <div className="member px-3 pb-9 pt-7" key={id}>
                    <div className="member-container">
                        <MemberInfoHeader info={info} />
                        <SubscriptionTable userId={id} />
                        <ChargesTable userId={id} displayTitle={true} />
                        <UserLogs userId={id} user={info} />
                    </div>
                </div>
            </>
        ),
        [id, info]
    );

    return id && Container;
}



function MemberInfoHeader({ info }) {
    return (
        <div className="member-info mt-3 mb-4">
            <UserAvatar user={info} size={95} loading={!info} />
            <div>
                <h2 className="member-name" >
                    <h2>{info?.username}</h2>
                    {info?.nickname &&
                        <span style={{ display: "flex", opacity: 0.6, gap: "2px", alignItems: "center" }}>
                            <AlternateEmailRounded style={{ width: "18px", height: "18px" }} />
                            {info?.nickname}
                        </span>
                    }</h2>
                <div className="member-info-details" style={{marginTop: '2px'}}>
                    <MailRounded style={{height: '16px', marginRight: '-3px', width: "16px"}} />
                    <span>{info?.email?.address}</span>
                </div>
                <div className="member-info-details" style={{marginTop: '2px'}}>
                    <PhoneRounded style={{height: '16px', marginRight: '-3px', width: "16px"}} />
                    <span>{info?.phone?.phone ? Parser.phone(info.phone) : '-'}</span>
                </div>
                {
                    info?.document && <div className="member-info-details" style={{marginTop: '2px'}}>
                        <FingerprintRounded style={{height: '16px', marginRight: '-3px', width: "16px"}} />
                        <span>{Parser.cpf(info?.document)}</span>
                    </div>
                }
            </div>
        </div>
    );
}
